import React from 'react';
import clsx from 'classnames';
import styles from './MobileTrackerItem.module.scss';
import { parseLocalDate } from '../../../utils/date';

export interface TrackerEventData {
  id: string;
  documentName: string;
  dealName: string;
  eventType: string;
  displayStatus?: string; // Exact status text from desktop ("Completed", "Past Due", "Upcoming")
  statusClass?: string; // Status class for styling
  criticalDate: string;
  dateDisplay?: string; // Same formatted date as desktop
  daysAway?: string; // Same relative time as desktop
  daysUntilDue?: number | null; // Days until due for fallback calculation
  onClick?: () => void;
}

interface MobileTrackerItemProps {
  event: TrackerEventData;
  onClick?: () => void;
}

const getStatusBadgeClass = (statusClass?: string) => {
  // Map status class to mobile styles
  if (statusClass === 'completed') return styles.trackerItem__statusCompleted;
  if (statusClass === 'past_due') return styles.trackerItem__statusPastDue;
  if (statusClass === 'upcoming') return styles.trackerItem__statusActive;
  return styles.trackerItem__statusActive;
};

const formatEventType = (eventType: string): string => {
  return eventType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const MobileTrackerItem: React.FC<MobileTrackerItemProps> = ({ event, onClick }) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  // Use the same formatted date as desktop, fallback to local formatting if not provided.
  // parseLocalDate keeps date-only values ("YYYY-MM-DD") on the correct calendar day.
  const formattedDate = event.dateDisplay || parseLocalDate(event.criticalDate).format('MMM D, YYYY');

  // Use the same relative time as desktop, fallback to calculation if not provided
  const getDaysAwayText = () => {
    if (event.daysAway) {
      return `(${event.daysAway})`;
    }
    // Fallback calculation
    if (event.daysUntilDue === null) return '';
    if (event.daysUntilDue === 0) return '(Today)';
    if (event.daysUntilDue < 0) {
      return `(${Math.abs(event.daysUntilDue)}d ago)`;
    }
    return `(in ${event.daysUntilDue}d)`;
  };

  return (
    <div className={styles.trackerItem} onClick={handleClick}>
      <div className={styles.trackerItem__header}>
        <div className={styles.trackerItem__document}>{event.documentName}</div>
      </div>
      <div className={styles.trackerItem__body}>
        <div className={styles.trackerItem__deal}>Deal: {event.dealName}</div>
        <div className={styles.trackerItem__eventType}>{formatEventType(event.eventType)}</div>
      </div>
      <div className={styles.trackerItem__footer}>
        <div className={styles.trackerItem__dueDate}>
          Due: {formattedDate}
          <span className={styles.trackerItem__daysAway}>{getDaysAwayText()}</span>
        </div>
        <div
          className={clsx(
            styles.trackerItem__status,
            getStatusBadgeClass(event.statusClass)
          )}
        >
          {event.displayStatus || 'Upcoming'}
        </div>
      </div>
    </div>
  );
};